import { createContext, useContext } from "react";

export type CycloneCommunity = {
  displayName: string;
  feedCategoryFilter?: string[];
  feedSuperCategoryFilter?: string[];
  isPrimaryCommunity?: boolean;
  cycloneCommunityId: string;
  cycloneCommunityCacheId: number;
  cycloneCommunityPrefix?: string;
};

export type CycloneCommunities = Record<string, CycloneCommunity>;

export type NetworkCommunity = {
  displayName: string;
  domain: string;
  isPrimaryCommunity?: boolean;
  name: string;
  sailthruSlug?: string;
  subdomain: string | null;
  wpSiteId: number;
  isFeedOnlyCommunity?: boolean;
  cycloneCommunities?: CycloneCommunities;
};

export type Communities = {
  [key: number]: NetworkCommunity;
};

export const createCycloneCommunityIndex = (
  communities: Communities,
): CycloneCommunities => {
  const index: CycloneCommunities = {};

  for (const community of Object.values(communities)) {
    Object.assign(index, community.cycloneCommunities ?? {});
  }

  return index;
};

export interface CommunityContextType {
  currentOrigin: string;
  currentCommunityId: number;
  currentCycloneCommunityId: string;
  currentWpSiteId: number;
  communities: Communities;
  cycloneCommunitiesById: CycloneCommunities;
  currentCommunity: NetworkCommunity;
}

const CommunityContext = createContext<CommunityContextType | null>(null);

export const useCommunityContext = (): CommunityContextType => {
  const communityContext = useContext(CommunityContext);

  if (!communityContext) {
    throw new Error("Missing Community Context");
  }

  return communityContext;
};

export const useCurrentCommunity = (): NetworkCommunity => {
  return useCommunityContext().currentCommunity;
};

export const useCurrentCycloneCommunity = (): CycloneCommunity => {
  const { currentCycloneCommunityId, currentCommunity, communities } =
    useCommunityContext();

  // Check current community first, then fall back to the primary community.
  // Sub-communities (e.g. Eater city sites) don't have their own cycloneCommunities
  // and share the primary community's cyclone configuration.
  const currentCycloneCommunity =
    currentCommunity.cycloneCommunities?.[currentCycloneCommunityId] ??
    Object.values(communities).find((community) => community.isPrimaryCommunity)
      ?.cycloneCommunities?.[currentCycloneCommunityId];

  if (!currentCycloneCommunity) {
    throw new Error(
      `Cyclone community not found for current cyclone community ID: ${currentCycloneCommunityId}`,
    );
  }

  return currentCycloneCommunity;
};

export const useCommunityBySubdomain = (
  subdomain: string,
): NetworkCommunity | undefined => {
  const { communities } = useCommunityContext();

  return Object.values(communities).find(
    (community) => community.subdomain === subdomain,
  );
};

export const useCommunityByWpSiteId = (
  wpSiteId?: number,
): NetworkCommunity | undefined => {
  const { communities } = useCommunityContext();

  return Object.values(communities).find(
    (community) => community.wpSiteId === wpSiteId,
  );
};

export const useCommunityByName = (
  name: string,
): NetworkCommunity | undefined => {
  const { communities } = useCommunityContext();

  return Object.values(communities).find(
    (community) => community.name === name,
  );
};

export const useCommunityByDisplayName = (
  displayName: string,
): NetworkCommunity | undefined => {
  const { communities } = useCommunityContext();

  return Object.values(communities).find(
    (community) => community.displayName === displayName,
  );
};

export default CommunityContext;
